import i18n from "@/i18n";
import { ApiError, getErrorMessage } from "@/lib/api-client";
import { getSsoStatus } from "@/lib/api/sso";
import { authClient } from "@/lib/auth-client";
import { H2J_PROVIDER_ID } from "@/lib/sso-accounts";

export { H2J_PROVIDER_ID };

/** Only same-origin, relative paths are safe redirect targets. */
export function sanitizeRedirect(value: string | undefined): string | undefined {
  // Must start with a single "/". Reject "//" and "/\" (and "\") — browsers
  // normalize backslashes to "/", so both become protocol-relative URLs to
  // an attacker-controlled host.
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) {
    return undefined;
  }
  return value;
}

/** The OAuth callback runs on the API host, so the way back needs this app's origin. */
function absoluteUrl(path: string): string {
  return new URL(path, window.location.origin).toString();
}

/**
 * Sends the person to Login H2J and back to `returnTo` (a path already run
 * through `sanitizeRedirect`). A callback failure comes back to the sign-in
 * screen as `?error=<code>`.
 */
export async function signInWithH2j(returnTo: string): Promise<void> {
  const res = await authClient.signIn.oauth2({
    providerId: H2J_PROVIDER_ID,
    callbackURL: absoluteUrl(returnTo),
    errorCallbackURL: absoluteUrl(`/login?redirect=${encodeURIComponent(returnTo)}`),
  });
  if (res.error) throw new Error(res.error.message ?? "Login H2J sign-in failed");
}

/**
 * The Login H2J logout URL for the CURRENT session. It must be read right before
 * signing out (the session is what defines it) and never cached. A failed read
 * only means a plain local sign-out.
 */
export async function fetchEndSessionUrl(): Promise<string | null> {
  try {
    return (await getSsoStatus()).endSessionUrl;
  } catch {
    return null;
  }
}

// Server messages are English; these codes have a localized text in `errors`.
const TRANSLATED_ERROR_CODES = new Set([
  "ALREADY_MEMBER",
  "USER_BLOCKED",
  "ALREADY_ACTIVATED",
  "NOT_PENDING_ACTIVATION",
  "MANAGED_BY_H2J",
  "SSO_NOT_CONFIGURED",
  "QUOTA_EXCEEDED",
]);

/** The toast text of a failed Login H2J invitation action. */
export function ssoErrorMessage(err: unknown, fallback: string): string {
  const code = err instanceof ApiError ? err.code : null;
  const translated =
    code && TRANSLATED_ERROR_CODES.has(code) ? i18n.t(`errors:${code}`, { defaultValue: "" }) : "";
  return translated || getErrorMessage(err, fallback);
}
